import React from 'react';
import { Box, Button, List, ListItem, Popover, Skeleton, Stack, Typography, Divider } from '@mui/material';
import styles from '../../../../../../../custom/style.module.css';
import flightStyles from 'components/pages/flights/tabs/flightreport/reports.module.css';
import { dateTolocale } from 'utils/converter';
import http from 'utils/http';
import { grey } from '@mui/material/colors';

class ConsigneeHistory extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      loadingHistory: false,
      history_data: [],
    };
  }

  fetchHistory = (e) => {
    this.setState({
      anchorEl: e.currentTarget,
      loadingHistory: true 
    });
    http.get(`change_log/consignee/${this.props.id}`)
      .then(({ data }) => {
        if (data.status === 'success') {
          this.setState({
            history_data: data.data,
            loadingHistory: false
          });
        } else {
          this.setState({ loadingHistory: false });
        }
      })
      .catch(() => {
        this.setState({ loadingHistory: false });
      });
  };

  closeHistory = () => {
    this.setState({ anchorEl: null });
  };

  getLabel = (key) => {
    const labels = {
      name: 'Name',
      contactName: 'Contact Name',
      email: 'E-mail',
      address: 'Address 1',
      address2: 'Address 2',
      city: 'City',
      state: 'State',
      zip: 'Zip',
      phone: 'Phone',
      mails: 'Email List',
      notification_emails: 'Notification Emails',
      verification_status: 'Status',
    };
    return labels[key] || key;
  };

  renderRegistrationFields = (log) => {
    // Check if registration was completed without any changes (just verification)
    if (log.new_details?.status === 'verified' && Object.keys(log.new_details).length === 2) {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant='caption' component='div' sx={{
            lineHeight: 1.6,
            fontSize: '0.75rem',
            fontStyle: 'italic',
            color: grey[600]
          }}>
            No changes
          </Typography>
        </Box>
      );
    }

    // For registration completed, filter out fields that didn't really change (null vs empty)
    const fieldsToShow = ['name', 'contactName', 'email', 'phone', 'address', 'address2', 'city', 'state', 'zip'];
    const fieldsWithChanges = fieldsToShow.filter(key => {
      // If it's in the log, check if it has a real change
      if (log.prev_details && Object.prototype.hasOwnProperty.call(log.prev_details, key)) {
        return this.hasRealChange(key, log);
      }
      // If no prev_details, show if new value is not empty
      return this.normalizeValue(log.new_details?.[key]) !== null;
    });

    // If no fields have real changes, show "No changes"
    if (fieldsWithChanges.length === 0 && !log.new_details?.mails) {
      return (
        <Box sx={{ mt: 1 }}>
          <Typography variant='caption' component='div' sx={{
            lineHeight: 1.6,
            fontSize: '0.75rem',
            fontStyle: 'italic',
            color: grey[600]
          }}>
            No changes
          </Typography>
        </Box>
      );
    }
    
    return (
      <Box sx={{ mt: 1 }}>
        {fieldsWithChanges.map((key) => {
          const value = log.new_details?.[key];
          
          return (
            <Typography key={key} variant='caption' component='div' sx={{
              lineHeight: 1.6,
              fontSize: '0.75rem' 
            }}>
              <span style={{
                fontWeight: 600,
                color: grey[700] 
              }}>{this.getLabel(key)}: </span>
              <span style={{
                color: '#2e7d32',
                fontWeight: 600 
              }}>{value}</span>
            </Typography>
          );
        })}
        {/* Display notification emails like email list additions */}
        {log.new_details?.mails && this.renderMailChanges(log)}
      </Box>
    );
  };

  renderMailChanges = (log) => {
    const oldMailsStr = log.prev_details?.mails || '';
    const newMailsStr = log.new_details?.mails || '';

    const oldMails = oldMailsStr ? oldMailsStr.split(', ').filter(Boolean) : [];
    const newMails = newMailsStr ? newMailsStr.split(', ').filter(Boolean) : [];

    const added = newMails.filter(m => !oldMails.includes(m));
    const removed = oldMails.filter(m => !newMails.includes(m));

    return (
      <Box sx={{ mt: 0.5 }}>
        {added.map((email, i) => (
          <Typography key={`add-${i}`} variant='caption' component='div' sx={{
            lineHeight: 1.6,
            fontSize: '0.75rem' 
          }}>
            <span style={{
              fontWeight: 600,
              color: grey[700] 
            }}>Email List</span>
            <span style={{
              margin: '0 6px',
              color: grey[500] 
            }}>&rarr;</span>
            <span style={{
              color: '#2e7d32',
              fontWeight: 600 
            }}>{email} (Added)</span>
          </Typography>
        ))}
        {removed.map((email, i) => (
          <Typography key={`rem-${i}`} variant='caption' component='div' sx={{
            lineHeight: 1.6,
            fontSize: '0.75rem' 
          }}>
            <span style={{
              fontWeight: 600,
              color: grey[700] 
            }}>Email List</span>
            <span style={{
              margin: '0 6px',
              color: grey[500] 
            }}>&rarr;</span>
            <span style={{
              color: '#d32f2f',
              fontWeight: 600 
            }}>{email} (Removed)</span>
          </Typography>
        ))}
      </Box>
    );
  };

  renderUpdateRegistrationEmail = (log) => {
    const email = log.new_details?.email_sent_to;
    
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant='caption' component='div' sx={{
          lineHeight: 1.6,
          fontSize: '0.75rem' 
        }}>
          <span style={{
            fontWeight: 600,
            color: grey[700] 
          }}>Email sent to: </span>
          <span style={{
            color: '#2e7d32',
            fontWeight: 600 
          }}>{email}</span>
        </Typography>
      </Box>
    );
  };

  // Helper to normalize empty values (treat null, undefined, empty string, and whitespace as equivalent)
  normalizeValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return null;
    }
    return value;
  };

  // Check if a field has a meaningful change
  hasRealChange = (key, log) => {
    const oldValue = this.normalizeValue(log.prev_details?.[key]);
    const newValue = this.normalizeValue(log.new_details?.[key]);
    return oldValue !== newValue;
  };

  // Filter out fields that don't have real changes
  getFieldsWithRealChanges = (log) => {
    if (!log.update_on) return [];
    const fields = log.update_on.split(', ').map(f => f.trim()).filter(Boolean);
    return fields.filter(key => this.hasRealChange(key, log));
  };

  render() {
    return (
      <>
        <Button
          title='History'
          size='small'
          disableElevation
          variant='contained'
          color='secondary'
          className={styles.historyIcon}
          sx={{ ml: 2 }}
          onClick={this.fetchHistory}
        >
          H
        </Button>
        <Popover
          open={Boolean(this.state.anchorEl)}
          anchorEl={this.state.anchorEl}
          onClose={this.closeHistory}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <List sx={{
            width: '100%',
            maxWidth: 600,
            minWidth: 500,
            bgcolor: 'background.paper',
            p: 2,
            maxHeight: 500,
            overflowY: 'auto'
          }}>
            {!this.state.loadingHistory ? (this.state.history_data.length > 0 ? this.state.history_data.map((log, index) => (
              <ListItem key={index} className={flightStyles.historyListItem} sx={{
                mb: 2,
                p: 1.5,
                borderRadius: '8px',
                bgcolor: '#f5f5f5',
                border: '1px solid #e0e0e0',
                flexDirection: 'column',
                alignItems: 'stretch' 
              }}>
                <Stack direction='row' spacing={1.5} alignItems='flex-start' mb={(log.operation === 'Registration Email Sent' || log.operation === 'Update Registration Email Sent' || log.operation === 'Registration Completed' || log.operation === 'Created Consignee') ? 0 : 1}>
                  {/* <Typography
                    variant='span'
                    color='white'
                    bgcolor='#092b4a'
                    textTransform={'uppercase'}
                    fontSize='9px'
                    fontWeight={800}
                    px={0.6}
                    py={0.3}
                    borderRadius={'3px'}
                    lineHeight={'normal'}
                    minWidth={'80px'}
                    textAlign={'center'}
                    sx={{ mt: '2px' }}
                  >
                    CONSIGNEE
                  </Typography> */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant='caption' color='primary' fontWeight={600} fontSize='12px' display='block'>
                      {log.operation || 'N/A'}
                    </Typography>
                    <Typography variant='caption' color='textSecondary' fontSize='0.7rem' fontWeight={500}>
                      {dateTolocale(log.updated_at, 'DD MMM YYYY')} | {log.updated_by}
                    </Typography>
                  </Box>
                </Stack>
                {/* Show different content for Registration/Create vs Update events */}
                {log.operation === 'Update Registration Email Sent' ? (
                  // For Update Registration Email Sent, show simple message
                  <>
                    <Divider sx={{ mb: 1 }} />
                    {this.renderUpdateRegistrationEmail(log)}
                  </>
                ) : (log.operation === 'Registration Email Sent' || log.operation === 'Registration Completed' || log.operation === 'Created Consignee') ? (
                  // For Registration/Create events, show clean field list without arrows
                  <>
                    <Divider sx={{ mb: 1 }} />
                    {this.renderRegistrationFields(log)}
                  </>
                ) : (
                  // For Update events, show full before/after comparison
                  <>
                    <Divider sx={{ mb: 1 }} />
                    <Box>
                      {(() => {
                        // Filter out fields that don't have real changes (null vs empty string)
                        const fieldsWithChanges = this.getFieldsWithRealChanges(log);
                        
                        if (fieldsWithChanges.length === 0) {
                          return (
                            <Typography variant='caption' component='div' sx={{
                              lineHeight: 1.6,
                              fontSize: '0.75rem',
                              fontStyle: 'italic',
                              color: grey[600]
                            }}>
                              No changes
                            </Typography>
                          );
                        }
                        
                        return fieldsWithChanges.map((key) => {
                          if (key === 'mails') {
                            return <React.Fragment key={key}>{this.renderMailChanges(log)}</React.Fragment>;
                          }
                          return (
                            <Typography key={key} variant='caption' component='div' sx={{
                              lineHeight: 1.6,
                              fontSize: '0.75rem',
                              wordBreak: 'break-all'
                            }}>
                              <span style={{
                                fontWeight: 600,
                                color: grey[700] 
                              }}>{this.getLabel(key)}</span>
                              <span style={{
                                margin: '0 6px',
                                color: grey[500] 
                              }}>&rarr;</span>
                              <span style={{ color: '#d32f2f' }}>{log.prev_details?.[key] || 'NA'}</span>
                              <span style={{
                                margin: '0 6px',
                                color: grey[500] 
                              }}>&rarr;</span>
                              <span style={{
                                color: '#2e7d32',
                                fontWeight: 600 
                              }}>{log.new_details[key] || 'NA'}</span>
                            </Typography>
                          );
                        });
                      })()}
                    </Box>
                  </>
                )}
              </ListItem>
            )) :
              <Box sx={{
                textAlign: 'center',
                width: '100%',
                p: 2
              }}>
                <Typography variant='span' color='textSecondary' fontSize='0.8rem'>No History Found</Typography>
              </Box>
            ) :
              Array.from({ length: 3 }).map((_, index) => (
                <ListItem key={index} sx={{
                  mb: 2,
                  p: 1.5 
                }}>
                  <Skeleton variant='rectangular' width='100%' height={80} />
                </ListItem>
              ))
            }
          </List>
        </Popover>
      </>
    );
  }
}

export default ConsigneeHistory;
